import { useState, useEffect } from "react";
import { Bell, Crown, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "react-router-dom";

type Notification = {
  id: string;
  notification_type: string;
  message: string;
  read: boolean;
  created_at: string;
};

const PremiumNotificationBell = ({ buttonClassName, iconClassName }: { buttonClassName?: string; iconClassName?: string } = {}) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    (supabase as any)
      .from("premium_notifications")
      .select("id, notification_type, message, read, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }: any) => {
        if (data) setNotifications(data);
      });
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllRead = async () => {
    if (!user) return;
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    await (supabase as any)
      .from("premium_notifications")
      .update({ read: true })
      .in("id", unreadIds);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  if (!user || notifications.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen(!open); if (!open) markAllRead(); }}
        className={buttonClassName || "relative p-2 rounded-xl hover:bg-muted transition-colors"}
      >
        <Bell className={iconClassName || "w-5 h-5 text-muted-foreground"} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto z-50 rounded-2xl border-2 border-border bg-card shadow-xl">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <span className="font-bold text-sm text-foreground">Notifications</span>
              <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-muted">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            {notifications.map((n) => (
              <div key={n.id} className={`p-3 border-b border-border/50 ${!n.read ? "bg-primary/5" : ""}`}>
                <p className="text-xs text-muted-foreground mb-1">
                  {new Date(n.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </p>
                <p className="text-sm text-foreground">{n.message}</p>
                {n.notification_type !== "expired" ? null : (
                  <Link to="/tarifs" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline">
                    <Crown className="w-3 h-3" /> Renouveler maintenant
                  </Link>
                )}
                {n.notification_type.startsWith("reminder_") && (
                  <Link to="/tarifs" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline">
                    <Crown className="w-3 h-3" /> Renouveler maintenant
                  </Link>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PremiumNotificationBell;
